<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { stores } from '@/util/stores.js';
import { useAnnouncementsStore, isAnnouncementActive, readAnnouncementContent } from '@/store/announcementsStore';
import AnnouncementContent from '@/components/AnnouncementContent.vue';
import EGS_Tooltip from '@/components/Tooltip/Tooltip.vue';

const announcements = useAnnouncementsStore();
const showBanner = computed(() => stores.authStore.showProfileIncompleteNotice);
const requiredText = 'A profile is considered complete if the following are filled in: First & Last name, date of birth, gender, primary email and primary phone number';
const now = ref(Date.now());
const visibleAnnouncements = computed(() => announcements.active.filter((item) => isAnnouncementActive(item, now.value)));
let timer: ReturnType<typeof setInterval> | undefined;

watch(
	() => (stores.authStore.isAuthenticated ? (stores.authStore.userInfo?.userNumber ?? null) : null),
	(userNumber) => {
		announcements.setSession(userNumber);
		void announcements.refresh();
	},
	{ immediate: true }
);

function refresh() {
	now.value = Date.now();
	if (document.visibilityState === 'visible') void announcements.refresh();
}

onMounted(() => {
	timer = setInterval(refresh, 30_000);
	window.addEventListener('focus', refresh);
});
onUnmounted(() => {
	clearInterval(timer);
	window.removeEventListener('focus', refresh);
	announcements.setSession(null);
});
</script>

<template>
	<div v-if="showBanner" aria-label="System announcement" class="relative z-[996] border border-amber-200 bg-amber-100 px-6 py-4 text-center font-medium text-amber-800 shadow-sm">
		<div class="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
			<span>Your Profile is Incomplete</span>
			<span class="inline-flex items-center">
				<EGS_Tooltip :text="requiredText" position="bottom">
					<font-awesome-icon icon="circle-info" class="cursor-help" />
				</EGS_Tooltip>
			</span>
			<router-link to="/app/profile" class="font-bold text-cyan-900 underline hover:no-underline"> Complete it now. </router-link>
		</div>
	</div>
	<div
		v-for="item in visibleAnnouncements"
		:key="item.id"
		:aria-label="item.announcementType === 'system' ? 'System announcement' : 'Temporary announcement'"
		class="relative z-[996] border px-6 py-4 text-center font-medium shadow-sm"
		:class="item.announcementType === 'system' ? 'border-sky-200 bg-sky-100 text-sky-900' : 'border-amber-200 bg-amber-100 text-amber-800'"
	>
		<div class="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
			<AnnouncementContent :content="readAnnouncementContent(item.text)" />
		</div>
	</div>
	<div v-if="announcements.error" class="px-6 py-2 text-center text-sm text-color-secondary" role="status">
		Announcements are currently unavailable.
		<button type="button" class="underline" @click="announcements.refresh()">Retry</button>
	</div>
</template>
